import { useState } from 'react';
import { Button, Drawer, Input, Segmented, useToast } from '@hana/ui';
import { useT } from '@hana/i18n';
import { dupsOf, parseBan } from './placeForm';
import { WH_VEHICLES, fmtRadius } from './places';
import type { Place, PlaceKind } from './types';
import s from './places.module.css';
import './i18n';

// Form Sửa / Thêm điểm · kho (Mint-TMS-Places, drawers.place). Chỉ giả lập: Lưu / Xoá báo toast, không ghi DB.
const RADII = ['300', '500', '1300'] as const;
const DEP_KINDS: readonly PlaceKind[] = ['Cảng', 'ICD', 'Depot'];

export function PlaceForm({ open, onClose, wh, place, all }: { open: boolean; onClose: () => void; wh: boolean; place: Place | null; all: readonly Place[] }) {
  const t = useT();
  const toast = useToast();
  const [v, setV] = useState<Record<string, string>>({});
  const [veh, setVeh] = useState<string[] | null>(null);
  const [kind, setKind] = useState<PlaceKind | null>(null);
  const [radius, setRadius] = useState<string | null>(null);
  const [err, setErr] = useState(false);
  const [dupOk, setDupOk] = useState(false);
  const [geo, setGeo] = useState(false);
  const r0 = place?.restrict;
  const get = (k: string, d: string) => (v[k] !== undefined ? v[k] : d);
  const set = (k: string) => (e: { target: { value: string } }) => { setV((o) => ({ ...o, [k]: e.target.value })); setDupOk(false); };
  const xyDef = place && place.lat != null ? `${place.lat}, ${place.lng}` : '';
  const xy = get('xy', xyDef);
  const dups = dupsOf(xy, all, place?.code ?? null);
  const banP = parseBan(get('ban', (r0?.ban ?? []).join(', ')));
  const name = get('name', place?.name ?? '');
  const curVeh = veh ?? r0?.veh ?? [];
  const curKind = kind ?? (place?.kind && DEP_KINDS.includes(place.kind) ? place.kind : null);
  const curRad = radius ?? (place?.radius ? String(place.radius) : wh ? '300' : '500');

  const close = () => { setV({}); setVeh(null); setKind(null); setRadius(null); setErr(false); setDupOk(false); setGeo(false); onClose(); };
  const save = () => {
    if (!name.trim()) { setErr(true); return; }
    if (wh && banP.bad) return;
    if (dups.length && !dupOk) { setDupOk(true); return; }
    toast({ msg: t('plform.saved', { name }) + (dups.length ? t('plform.savedDup', { n: dups.length }) : ''), tone: 'ok' });
    close();
  };
  const hint = dups.length
    ? t('plform.dupHint', { list: dups.map((d) => `${d.place.name} · ${d.metres} m`).join(' · ') })
    : geo ? t('plform.geoOk') : t('plform.xyHint');

  const title = place ? place.name : t(wh ? 'plform.addWh' : 'plform.addDep');
  return (
    <Drawer
      open={open}
      onOpenChange={(o) => { if (!o) close(); }}
      title={title}
      description={t('plform.sub')}
      footer={
        <>
          <span className={s.dim}>{t('plform.foot')}</span>
          {place ? <Button variant="danger" onClick={() => { toast({ msg: t('plform.deleted', { name: place.name }) }); close(); }}>{t('plform.delete')}</Button> : <Button variant="ghost" onClick={close}>{t('plform.cancel')}</Button>}
          <Button variant="primary" onClick={save}>{t('plform.save')}</Button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); save(); }} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label={t(wh ? 'plform.whName' : 'plform.name')} required value={name} onChange={set('name')} error={err && !name.trim() ? t(wh ? 'plform.whNameErr' : 'plform.nameErr') : undefined} />
        <Input label={t(wh ? 'plform.whCode' : 'plform.code')} placeholder={wh ? 'WH0341' : 'DP26NNNN'} value={get('code', place?.code ?? '')} onChange={set('code')} hint={place ? undefined : t(wh ? 'plform.codeAuto' : 'plform.depotAuto')} />
        {!wh && (
          <div role="radiogroup" aria-label={t('plform.kind')}>
            <Segmented kind="toggle" label={t('plform.kind')} items={DEP_KINDS.map((k) => ({ key: k, label: k }))} value={(curKind ?? '') as PlaceKind} onChange={setKind} />
          </div>
        )}
        {wh
          ? <Input label={t('plform.address')} placeholder="Số nhà, đường, KCN" value={get('adr', place?.address ?? '')} onChange={set('adr')} />
          : <Input label={t('plform.area')} placeholder="Cát Lái · Thủ Đức" value={get('area', place?.area ?? '')} onChange={set('area')} />}
        <Input label={t(wh ? 'plform.whHours' : 'plform.hours')} placeholder={wh ? '07:00–17:00' : '24/7 hoặc 06:00–22:00'} value={get('hrs', place?.hours ?? '')} onChange={set('hrs')} hint={wh ? undefined : t('plform.newField')} />
        {wh ? <Input label={t('plform.recvNote')} placeholder="Báo cổng KCN, cắm điện mooc lạnh…" value={get('note', place?.note ?? '')} onChange={set('note')} />
          : <Input label={t('plform.carriers')} placeholder="ONE CMA/RF EMC/DC" value={get('cr', place?.carriers ?? '')} onChange={set('cr')} hint={t('plform.carriersHint')} />}
        {wh && (
          <>
            <h4>{t('plform.restrict')}</h4>
            <Input label={t('plform.ban')} placeholder="06:00–09:00, 16:00–20:00" value={get('ban', (r0?.ban ?? []).join(', '))} onChange={set('ban')} error={banP.bad ? t('plform.banBad') : undefined} hint={banP.ok.length ? t('plform.banN', { n: banP.ok.length }) : t('plform.banMulti')} />
            <Input label={t('plform.ton')} placeholder="Trống = không giới hạn" value={get('ton', r0?.ton ? String(r0.ton) : '')} onChange={set('ton')} />
            <div role="group" aria-label={t('plform.veh')}>
              <Segmented kind="toggle" label={t('plform.veh')} items={WH_VEHICLES.map((k) => ({ key: k, label: curVeh.includes(k) ? `✓ ${k}` : k }))} value={'' as never} onChange={(k) => setVeh(curVeh.includes(k) ? curVeh.filter((x) => x !== k) : [...curVeh, k])} />
              <div className={s.dim}>{t('plform.vehHint')}</div>
            </div>
            <Input label={t('plform.road')} placeholder="Cầu Phú Khương, nội đô…" value={get('road', r0?.road ?? '')} onChange={set('road')} />
          </>
        )}
        <h4>{t('plform.pos')}</h4>
        <Input label={t('plform.xy')} placeholder="10.76, 106.78" value={xy} onChange={set('xy')} hint={hint} />
        <Segmented kind="toggle" label={t('plform.radius')} items={RADII.map((k) => ({ key: k, label: fmtRadius(Number(k)) }))} value={curRad as (typeof RADII)[number]} onChange={setRadius} />
        {dups.length > 0 && dupOk && <div role="status">{t('plform.dupAgain', { n: dups.length })}</div>}
        <Button variant="secondary" onClick={() => { setGeo(true); setDupOk(false); setV((o) => ({ ...o, xy: '10.7735, 106.7976' })); }}>{t('plform.useGps')}</Button>
        <Button variant="secondary" onClick={() => toast({ msg: t('plform.reverse') })}>{t('plform.fill')}</Button>
        {!wh && place && (
          <>
            <h4>{t('plform.gate')}</h4>
            <Input label="Mã ECUS" value={get('ecus', place.ecus ?? '')} onChange={set('ecus')} />
            <Input label={t('plform.eport')} placeholder="https://…" value={get('eport', place.eport ? t('plform.linked') : '')} onChange={set('eport')} />
          </>
        )}
      </form>
    </Drawer>
  );
}
